import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Layout } from "../components/Layout";
import { api } from "../api/client";
import type { WorkOrderDetail, WorkOrderPatch } from "../api/types";
import {
  WO_PRIORITIES,
  WO_STATUSES,
  formatDate,
  formatDateTime,
  formatWoId,
  woPriorityLabel,
  woStatusLabel,
} from "../lib/workorders";

export function WorkOrderDetailPage() {
  const { id } = useParams();
  const woId = Number(id);

  const detailQ = useQuery({
    queryKey: ["work-order", woId],
    queryFn: () => api.workOrders.get(woId),
    enabled: Number.isInteger(woId),
  });

  if (detailQ.isLoading) {
    return (
      <Layout title="Work Order Details">
        <p className="sub">Loading…</p>
      </Layout>
    );
  }
  if (detailQ.isError || !detailQ.data) {
    return (
      <Layout title="Work Order Details">
        <section className="card">
          <p>That work order could not be found.</p>
          <Link to="/work-orders" className="btn btn-ghost btn-xl">
            Back to Work Orders
          </Link>
        </section>
      </Layout>
    );
  }

  return <WorkOrderDetailView wo={detailQ.data.workOrder} />;
}

function WorkOrderDetailView({ wo }: { wo: WorkOrderDetail }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [status, setStatus] = useState(wo.status);
  const [priority, setPriority] = useState(wo.priority);
  const [dueDate, setDueDate] = useState(wo.dueDate ?? "");
  const [assignedDate, setAssignedDate] = useState(wo.assignedDate ?? "");
  const [assignedId, setAssignedId] = useState(
    wo.assignedMaintenanceId ? String(wo.assignedMaintenanceId) : "",
  );
  const [description, setDescription] = useState(wo.description);
  const [recurring, setRecurring] = useState(wo.recurring);
  const [recurringType, setRecurringType] = useState(wo.recurringType ?? "monthly");
  const [recurringDate, setRecurringDate] = useState(wo.recurringDate ?? "");
  const [adminAlertDate, setAdminAlertDate] = useState(wo.adminAlertDate ?? "");
  const [saved, setSaved] = useState(false);

  const maintenanceQ = useQuery({
    queryKey: ["ref", "maintenance"],
    queryFn: () => api.reference.maintenance(),
  });

  const saveMut = useMutation({
    mutationFn: (patch: WorkOrderPatch) => api.workOrders.update(wo.id, patch),
    onSuccess: () => {
      setSaved(true);
      queryClient.invalidateQueries({ queryKey: ["work-order", wo.id] });
      queryClient.invalidateQueries({ queryKey: ["work-orders"] });
    },
  });

  function onSave() {
    setSaved(false);
    saveMut.mutate({
      status,
      priority,
      description: description.trim(),
      dueDate: dueDate || null,
      assignedDate: assignedDate || null,
      assignedMaintenanceId: assignedId ? Number(assignedId) : null,
      recurring,
      recurringType: recurring ? recurringType : null,
      recurringDate: recurring ? recurringDate || null : null,
      adminAlertDate: adminAlertDate || null,
    });
  }

  return (
    <Layout title="Work Order Details">
      <section className="card">
        <div className="detail-head">
          <div>
            <h2>
              {formatWoId(wo.id)} — {wo.title}
            </h2>
            <p className="sub">
              {wo.where} · {wo.buildingName} · Created {formatDate(wo.createdAt)} by{" "}
              {wo.createdByName}
            </p>
          </div>
          <select
            className="inline-edit"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            aria-label="Status"
          >
            {WO_STATUSES.map((s) => (
              <option key={s} value={s}>
                {woStatusLabel(s)}
              </option>
            ))}
          </select>
        </div>

        <p className="help-line">
          Edit any field below and tap <strong>Save changes</strong>. The status dropdown above is
          editable too.
        </p>

        <div className="two-col">
          <label className="field">
            <span>Priority</span>
            <select value={priority} onChange={(e) => setPriority(e.target.value)}>
              {WO_PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {woPriorityLabel(p)}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>To be completed by</span>
            <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
          </label>
        </div>

        <div className="two-col">
          <label className="field">
            <span>Assigned date</span>
            <input
              type="date"
              value={assignedDate}
              onChange={(e) => setAssignedDate(e.target.value)}
            />
          </label>
          <label className="field">
            <span>Admin alert date</span>
            <input
              type="date"
              value={adminAlertDate}
              onChange={(e) => setAdminAlertDate(e.target.value)}
            />
          </label>
        </div>

        <div className="two-col">
          <label className="field">
            <span>Start time</span>
            <input value={wo.startTime ? formatDateTime(wo.startTime) : "Not started"} readOnly />
          </label>
          <label className="field">
            <span>Completion time</span>
            <input
              value={wo.completionTime ? formatDateTime(wo.completionTime) : "Not completed"}
              readOnly
            />
          </label>
        </div>

        {wo.tenantNoticeRequired && (
          <div className="notice-panel">
            <div>
              <strong>Tenant 24-hour notice</strong>
              <p className="sub">
                {wo.tenantNoticeConfirmedAt
                  ? `Confirmed ${formatDateTime(wo.tenantNoticeConfirmedAt)}.`
                  : "Notice has not been confirmed yet."}
              </p>
            </div>
            {!wo.tenantNoticeConfirmedAt && (
              <button
                type="button"
                className="btn btn-ghost btn-xl"
                onClick={() =>
                  saveMut.mutate({
                    tenantNoticeConfirmed: true,
                  })
                }
                disabled={saveMut.isPending}
              >
                Confirm notice
              </button>
            )}
          </div>
        )}

        <label className="checkbox-line">
          <input
            type="checkbox"
            checked={recurring}
            onChange={(e) => setRecurring(e.target.checked)}
          />
          <span>Recurring work order</span>
        </label>

        {recurring && (
          <div className="two-col">
            <label className="field">
              <span>Recurring type</span>
              <select value={recurringType} onChange={(e) => setRecurringType(e.target.value)}>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="yearly">Yearly</option>
              </select>
            </label>
            <label className="field">
              <span>Recurring date</span>
              <input
                type="date"
                value={recurringDate}
                onChange={(e) => setRecurringDate(e.target.value)}
              />
            </label>
          </div>
        )}

        <label className="field">
          <span>Assigned to</span>
          <select value={assignedId} onChange={(e) => setAssignedId(e.target.value)}>
            <option value="">Unassigned</option>
            {(maintenanceQ.data?.maintenance ?? []).map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          <span>Description</span>
          <textarea
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>

        <div className="form-actions">
          {saved && !saveMut.isPending && <span className="saved-note">Saved.</span>}
          {saveMut.isError && <span className="field-error">Could not save. Try again.</span>}
          <a
            href="/work-orders"
            className="btn btn-ghost btn-xl"
            onClick={(e) => {
              e.preventDefault();
              navigate("/work-orders");
            }}
          >
            Back to list
          </a>
          <button
            className="btn btn-primary btn-xl"
            onClick={onSave}
            disabled={saveMut.isPending}
          >
            {saveMut.isPending ? "Saving…" : "Save changes"}
          </button>
        </div>
      </section>

      <section className="card">
        <h2>Supplies used</h2>
        {wo.suppliesUsed.length === 0 ? (
          <p className="sub">No supplies recorded by Maintenance.</p>
        ) : (
          <ul className="supply-list">
            {wo.suppliesUsed.map((s) => (
              <li key={s.id}>
                <span>
                  <strong>
                    {s.quantityUsed}
                    {s.unitOfMeasure ? ` ${s.unitOfMeasure}` : ""}
                  </strong>{" "}
                  — {s.name}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="card">
        <h2>History</h2>
        <ul className="timeline">
          <li>
            <strong>{formatDateTime(wo.createdAt)}</strong> — {wo.createdByName} created this work
            order.
          </li>
          {wo.history.map((h, i) => (
            <li key={i}>
              <strong>{formatDateTime(h.at)}</strong> —{" "}
              {h.kind === "status"
                ? `${h.actor} changed status from ${woStatusLabel(
                    h.from ?? "",
                  )} to ${woStatusLabel(h.to ?? "")}.`
                : `${h.actor}: ${h.text}`}
            </li>
          ))}
        </ul>
      </section>
    </Layout>
  );
}
